import { getTicket, listTickets, rejectFix, setStatus, updateTicket } from '@/services/api/serviceDesk';
import { z } from 'zod';
import { BrowserStore } from '@/utils/BrowserStore';
import { evictStaleItemCaches } from '@/components/Support/support_constants';

const CACHE_SCHEMA = z.object({
  items: z.array(z.record(z.string(), z.unknown())).default([]),
});

const CACHE_PREFIXES = {
  work: 'support_list_v2',
  desk: 'service_desk_items_v3',
};

const MERGE_AFTER_WRITE = ['updated_at', 'completed_at', 'lifecycle', 'waiting_since', 'first_response_at', 'resolution_at'];

function cacheKeyFor(scope, tenantSlug, userId) {
  if (!scope || !tenantSlug || userId == null) return null;
  return `${CACHE_PREFIXES[scope]}:${tenantSlug}:${userId}`;
}

function cacheStore(scope, tenantSlug, userId) {
  const key = cacheKeyFor(scope, tenantSlug, userId);
  return key ? new BrowserStore(key, CACHE_SCHEMA) : null;
}

function emptyCollection() {
  return {
    ids: [],
    loading: false,
    refreshing: false,
    fetchError: false,
    lastFetchedAt: null,
    cacheKey: null,
    paramsKey: null,
  };
}

const inFlight = {};

export default {
  namespaced: true,
  state: () => ({
    byId: {},
    pending: {},
    collections: {
      work: emptyCollection(),
      desk: emptyCollection(),
    },
  }),
  getters: {
    byId: (state) => state.byId,
    item: (state) => (id) => state.byId[id] || null,
    rows: (state) => (scope) => {
      const collection = state.collections[scope];
      if (!collection) return [];
      const rows = [];
      for (const id of collection.ids) {
        const row = state.byId[id];
        if (row) rows.push(row);
      }
      return rows;
    },
    collection: (state) => (scope) => state.collections[scope] || emptyCollection(),
  },
  mutations: {
    MERGE_ROWS(state, { scope, rows, cacheKey, paramsKey }) {
      const ids = [];
      for (const row of rows) {
        const existing = state.byId[row.id];
        if (existing) {
          const pendingFields = state.pending[row.id] || {};
          for (const key of Object.keys(row)) {
            if (pendingFields[key]) continue;
            existing[key] = row[key];
          }
        } else {
          state.byId[row.id] = { ...row };
        }
        ids.push(row.id);
      }
      const collection = state.collections[scope];
      const otherScopeIds = new Set();
      for (const [name, other] of Object.entries(state.collections)) {
        if (name !== scope) other.ids.forEach((id) => otherScopeIds.add(id));
      }
      const newIds = new Set(ids);
      for (const id of collection.ids) {
        if (!newIds.has(id) && !otherScopeIds.has(id) && !state.pending[id]) {
          delete state.byId[id];
        }
      }
      collection.ids = ids;
      collection.cacheKey = cacheKey;
      collection.paramsKey = paramsKey;
    },
    UPSERT(state, row) {
      if (!row || row.id == null) return;
      const existing = state.byId[row.id];
      if (existing) {
        const pendingFields = state.pending[row.id] || {};
        for (const key of Object.keys(row)) {
          if (pendingFields[key]) continue;
          existing[key] = row[key];
        }
      } else {
        state.byId[row.id] = { ...row };
      }
    },
    ADD_TO_SCOPE(state, { scope, id }) {
      const collection = state.collections[scope];
      if (collection && !collection.ids.includes(id)) collection.ids = [id, ...collection.ids];
    },
    REMOVE(state, id) {
      delete state.byId[id];
      delete state.pending[id];
      for (const collection of Object.values(state.collections)) {
        collection.ids = collection.ids.filter((x) => x !== id);
      }
    },
    PATCH_LOCAL(state, { id, fields }) {
      const row = state.byId[id];
      if (!row) return;
      Object.assign(row, fields);
    },
    SET_PENDING(state, { id, keys, value }) {
      const current = state.pending[id] || {};
      for (const key of keys) {
        if (value) current[key] = true;
        else delete current[key];
      }
      if (Object.keys(current).length) state.pending[id] = current;
      else delete state.pending[id];
    },
    SET_COLLECTION_STATE(state, { scope, ...fields }) {
      Object.assign(state.collections[scope], fields);
    },
    CLEAR_SCOPE(state, scope) {
      const collection = state.collections[scope];
      const otherScopeIds = new Set();
      for (const [name, other] of Object.entries(state.collections)) {
        if (name !== scope) other.ids.forEach((id) => otherScopeIds.add(id));
      }
      for (const id of collection.ids) {
        if (!otherScopeIds.has(id) && !state.pending[id]) delete state.byId[id];
      }
      state.collections[scope] = emptyCollection();
    },
  },
  actions: {
    hydrateFromCache({ commit, state }, { scope, tenantSlug, userId }) {
      const store = cacheStore(scope, tenantSlug, userId);
      if (!store) return false;
      const cached = store.load().items;
      if (!cached.length) return false;
      commit('MERGE_ROWS', { scope, rows: cached, cacheKey: store.key, paramsKey: state.collections[scope].paramsKey });
      return true;
    },
    async fetch({ commit, state }, { scope, params, tenantSlug, userId }) {
      const cacheKey = cacheKeyFor(scope, tenantSlug, userId);
      const paramsKey = JSON.stringify(params || {});
      const flightKey = `${scope}:${cacheKey}:${paramsKey}`;
      if (inFlight[flightKey]) return inFlight[flightKey];

      const collection = state.collections[scope];
      const hasRows = collection.ids.length > 0;
      commit('SET_COLLECTION_STATE', { scope, loading: !hasRows, refreshing: hasRows });

      const request = (async () => {
        try {
          const rows = await listTickets({ view: 'list', ...params });
          if (!Array.isArray(rows)) {
            throw new Error('Malformed /api/requirements/ response: expected an array');
          }
          commit('MERGE_ROWS', { scope, rows, cacheKey, paramsKey });
          commit('SET_COLLECTION_STATE', { scope, fetchError: false, lastFetchedAt: Date.now() });
          const store = cacheStore(scope, tenantSlug, userId);
          if (store) {
            evictStaleItemCaches(CACHE_PREFIXES[scope], store.key);
            store.save({ items: rows });
          }
          return true;
        } catch {
          commit('SET_COLLECTION_STATE', { scope, fetchError: true });
          return false;
        } finally {
          commit('SET_COLLECTION_STATE', { scope, loading: false, refreshing: false });
          delete inFlight[flightKey];
        }
      })();
      inFlight[flightKey] = request;
      return request;
    },
    async fetchOne({ commit }, id) {
      const data = await getTicket(id);
      commit('UPSERT', data);
      return data;
    },
    async rejectFix({ commit }, { id, body }) {
      const data = await rejectFix(id, body);
      commit('UPSERT', data);
      return data;
    },
    patchLocal({ commit }, { id, fields }) {
      commit('SET_PENDING', { id, keys: Object.keys(fields), value: true });
      commit('PATCH_LOCAL', { id, fields });
    },
    revert({ commit }, { id, fields }) {
      commit('PATCH_LOCAL', { id, fields });
      commit('SET_PENDING', { id, keys: Object.keys(fields), value: false });
    },
    async commit({ commit }, { id, payload, endpoint, localKeys }) {
      const keys = localKeys || Object.keys(payload);
      try {
        const data = endpoint === 'set-status' ? await setStatus(id, payload.status) : await updateTicket(id, payload);
        commit('SET_PENDING', { id, keys, value: false });
        if (data && typeof data === 'object') {
          const merged = {};
          for (const key of [...keys, ...MERGE_AFTER_WRITE]) {
            if (key in data) merged[key] = data[key];
          }
          commit('PATCH_LOCAL', { id, fields: merged });
        }
        return data;
      } catch (err) {
        commit('SET_PENDING', { id, keys, value: false });
        throw err;
      }
    },
    async update({ dispatch }, { id, payload, endpoint }) {
      await dispatch('patchLocal', { id, fields: payload });
      return dispatch('commit', { id, payload, endpoint });
    },
    upsert({ commit }, row) {
      commit('UPSERT', row);
    },
    addToScope({ commit }, { scope, row }) {
      commit('UPSERT', row);
      commit('ADD_TO_SCOPE', { scope, id: row.id });
    },
    remove({ commit }, id) {
      commit('REMOVE', id);
    },
    clearScope({ commit }, scope) {
      commit('CLEAR_SCOPE', scope);
    },
  },
};
